import React, { useState, useEffect } from 'react';
import {
    LayoutDashboard, Users, Package, ShoppingCart, Truck,
    ClipboardList, DollarSign, FileText, Building, Wallet,
    CreditCard, Settings, BarChart3, HelpCircle, Menu, X,
    LogOut, Plus, Edit, Trash2, User, Check, Download, Upload, Search,
    PhilippinePesoIcon, Calculator, CheckCircle, Eye,
    Factory, Sigma, AlertCircle, ScanFace, Filter, Group, TrendingUp,
    RefreshCw, BoxesIcon, Utensils, Ham, ShieldCheck, ShieldAlert, ArrowLeft,
    ChevronDown, FlaskConical, PackageSearch, XCircle, Circle,
    Calendar, CalendarDays, Lock, EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// Global fetch wrapper with auto-logout on 401
const fetchWithAuth = async (url, options = {}) => {
    const response = await fetch(url, options);

    if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.reload();
        throw new Error('Session expired. Please log in again.');
    }

    return response;
};

// ============================================
// Password Change Modal Component
// ============================================
const PasswordChangeModal = ({ isOpen, onClose, token, isMandatory = false }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            // Reset form when modal closes
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowCurrent(false);
            setShowNew(false);
            setShowConfirm(false);
            setError('');
            setSuccess('');
        }
    }, [isOpen]);

    const validatePassword = () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            setError('All fields are required');
            return false;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return false;
        }

        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return false;
        }

        if (currentPassword === newPassword) {
            setError('New password must be different from current password');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!validatePassword()) {
            return;
        }

        setLoading(true);

        try {
            const res = await fetchWithAuth(`${API_URL}/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                    confirm_password: confirmPassword
                })
            });

            const data = await res.json();

            if (res.ok) {
                setSuccess('Password changed successfully!');
                setTimeout(() => {
                    onClose(true); // Pass true to indicate success
                }, 1500);
            } else {
                setError(data.error || 'Failed to change password');
            }
        } catch (err) {
            setError('Error changing password. Please try again.');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
        },
        modal: {
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '480px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
            position: 'relative'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '2px solid #e5e7eb'
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0,
            flex: 1
        },
        closeButton: {
            background: 'none',
            border: 'none',
            cursor: isMandatory ? 'not-allowed' : 'pointer',
            padding: '4px',
            borderRadius: '4px',
            opacity: isMandatory ? 0.3 : 1
        },
        alert: {
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        alertError: {
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            border: '1px solid #ef4444'
        },
        alertSuccess: {
            backgroundColor: '#d1fae5',
            color: '#065f46',
            border: '1px solid #10b981'
        },
        alertWarning: {
            backgroundColor: '#fef3c7',
            color: '#92400e',
            border: '1px solid #f59e0b'
        },
        formGroup: {
            marginBottom: '20px'
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
        },
        inputWrapper: {
            position: 'relative'
        },
        input: {
            width: '100%',
            padding: '10px 40px 10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            boxSizing: 'border-box'
        },
        eyeButton: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#6b7280'
        },
        button: {
            width: '100%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            marginTop: '24px'
        },
        buttonDisabled: {
            backgroundColor: '#9ca3af',
            cursor: 'not-allowed'
        },
        passwordStrength: {
            marginTop: '8px',
            fontSize: '12px'
        },
        strengthWeak: {
            color: '#ef4444'
        },
        strengthMedium: {
            color: '#f59e0b'
        },
        strengthStrong: {
            color: '#10b981'
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return null;
        if (password.length < 8) return { text: 'Too short', style: styles.strengthWeak };
        // if (password.length < 8) return { text: 'Weak', style: styles.strengthWeak };
        if (password.length < 12) return { text: 'Medium', style: styles.strengthMedium };
        return { text: 'Strong', style: styles.strengthStrong };
    };

    const passwordStrength = getPasswordStrength(newPassword);

    return (
        <div style={styles.overlay} onClick={isMandatory ? undefined : () => onClose(false)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <Lock style={{ width: '28px', height: '28px', color: '#4f46e5' }} />
                    <h2 style={styles.title}>Change Password</h2>
                    {!isMandatory && (
                        <button onClick={() => onClose(false)} style={styles.closeButton}>
                            <X style={{ width: '24px', height: '24px' }} />
                        </button>
                    )}
                </div>

                {isMandatory && (
                    <div style={{ ...styles.alert, ...styles.alertWarning }}>
                        <AlertCircle style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                        <p style={{ margin: 0 }}>
                            You must change your password before continuing
                        </p>
                    </div>
                )}

                {error && (
                    <div style={{ ...styles.alert, ...styles.alertError }}>
                        <AlertCircle style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                        <p style={{ margin: 0 }}>{error}</p>
                    </div>
                )}

                {success && (
                    <div style={{ ...styles.alert, ...styles.alertSuccess }}>
                        <Check style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                        <p style={{ margin: 0 }}>{success}</p>
                    </div>
                )}

                <div onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Current Password *</label>
                        <div style={styles.inputWrapper}>
                            <input
                                type={showCurrent ? 'text' : 'password'}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                style={styles.input}
                                placeholder="Enter current password"
                                disabled={loading}
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrent(!showCurrent)}
                                style={styles.eyeButton}
                            >
                                {showCurrent ? (
                                    <EyeOff style={{ width: '20px', height: '20px' }} />
                                ) : (
                                    <Eye style={{ width: '20px', height: '20px' }} />
                                )}
                            </button>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>New Password *</label>
                        <div style={styles.inputWrapper}>
                            <input
                                type={showNew ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                style={styles.input}
                                placeholder="Enter new password"
                                disabled={loading}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowNew(!showNew)}
                                style={styles.eyeButton}
                            >
                                {showNew ? (
                                    <EyeOff style={{ width: '20px', height: '20px' }} />
                                ) : (
                                    <Eye style={{ width: '20px', height: '20px' }} />
                                )}
                            </button>
                        </div>
                        {passwordStrength && (
                            <div style={{ ...styles.passwordStrength, ...passwordStrength.style }}>
                                Password strength: {passwordStrength.text}
                            </div>
                        )}
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Confirm New Password *</label>
                        <div style={styles.inputWrapper}>
                            <input
                                type={showConfirm ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                style={styles.input}
                                placeholder="Confirm new password"
                                disabled={loading}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirm(!showConfirm)}
                                style={styles.eyeButton}
                            >
                                {showConfirm ? (
                                    <EyeOff style={{ width: '20px', height: '20px' }} />
                                ) : (
                                    <Eye style={{ width: '20px', height: '20px' }} />
                                )}
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={handleSubmit}
                        style={{
                            ...styles.button,
                            ...(loading ? styles.buttonDisabled : {})
                        }}
                        disabled={loading}
                    >
                        <Lock style={{ width: '18px', height: '18px' }} />
                        {loading ? 'Changing Password...' : 'Change Password'}
                    </button>
                </div>

                <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '13px', color: '#6b7280' }}>
                    <strong style={{ color: '#374151' }}>Password Requirements:</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                        <li>At least 8 characters long</li>
                        <li>Different from your current password</li>
                        <li>Both new password fields must match</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

// ============================================
// Password Change Page Component (for sidebar menu)
// ============================================
const PasswordChangePage = ({ token }) => {
    const [showModal, setShowModal] = useState(false);

    const styles = {
        container: {
            padding: '24px',
            backgroundColor: '#f3f4f6',
            minHeight: '100vh'
        },
        card: {
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            maxWidth: '600px',
            margin: '0 auto'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '2px solid #e5e7eb'
        },
        title: {
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
        },
        description: {
            fontSize: '15px',
            color: '#6b7280',
            marginBottom: '24px',
            lineHeight: '1.6'
        },
        button: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            backgroundColor: '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
        },
        infoBox: {
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#f0f9ff',
            borderRadius: '8px',
            border: '1px solid #bae6fd'
        },
        infoTitle: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#1e40af',
            marginBottom: '8px'
        },
        infoList: {
            margin: 0,
            paddingLeft: '20px',
            fontSize: '14px',
            color: '#1e3a8a'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.header}>
                    <Lock style={{ width: '32px', height: '32px', color: '#4f46e5' }} />
                    <h1 style={styles.title}>Change Password</h1>
                </div>

                <p style={styles.description}>
                    Keep your account secure by regularly updating your password. Make sure to choose a strong password that you don't use elsewhere.
                </p>

                <button onClick={() => setShowModal(true)} style={styles.button}>
                    <Lock style={{ width: '18px', height: '18px' }} />
                    Change Password
                </button>

                <div style={styles.infoBox}>
                    <div style={styles.infoTitle}>Security Tips:</div>
                    <ul style={styles.infoList}>
                        <li>Use a unique password for this account</li>
                        <li>Make it at least 8 characters long</li>
                        <li>Include a mix of letters, numbers, and symbols</li>
                        <li>Avoid using personal information</li>
                        <li>Don't share your password with anyone</li>
                    </ul>
                </div>
            </div>

            <PasswordChangeModal
                isOpen={showModal}
                onClose={(success) => {
                    setShowModal(false);
                    if (success) {
                        // Optionally show a success message
                        alert('Password changed successfully!');
                    }
                }}
                token={token}
                isMandatory={false}
            />
        </div>
    );
};

// ============================================
// Hook to check password status on app load
// ============================================
const usePasswordCheck = (token) => {
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkPasswordStatus = async () => {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const res = await fetchWithAuth(`${API_URL}/auth/check-password-status`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.ok) {
                    const data = await res.json();
                    setMustChangePassword(data.must_change_password);
                }
            } catch (err) {
                console.error('Error checking password status:', err);
            } finally {
                setLoading(false);
            }
        };

        checkPasswordStatus();
    }, [token]);

    return { mustChangePassword, setMustChangePassword, loading };
};

// Export components
export { PasswordChangeModal, PasswordChangePage, usePasswordCheck };